import { dapiFetchCache } from "@origin-digital/daxi";
import { ILPGOffer } from "@origin-digital/sales-common";
import { errorForm } from "@origin-digital/reporting-client";
import { FORM_NAME } from "../consts";
import { config } from "../config";
import {
  DismissableBannerType,
  IDismissableBannerOptions,
} from "../components/DismissableBanner/DismissableBanner";
import {
  IDeliverabilityStatus,
  ILPGOffersApiResponse,
} from "./lpgOffersAPI.types";

export type LPGOfferDeliverability = {
  offer?: ILPGOffer;
  deliverability?: IDeliverabilityStatus;
};

const findOfferByOfferType = (offers: ILPGOffer[], isRefill: boolean) =>
  offers.find((offer) => offer.isRefill === isRefill);

export const getLPGOffer = async (
  suburb: string,
  postcode: string
): Promise<LPGOfferDeliverability | undefined> => {
  const response = await dapiFetchCache<ILPGOffersApiResponse>(
    `${config.lpgOffersApiProps.url}/crosssell?suburb=${suburb}&postcode=${postcode}`,
    {
      ttlInMin: 30,
    }
  );

  if (response?.data?.offers) {
    const { offers, deliverability } = response.data;

    if (
      deliverability?.exchange?.status &&
      deliverability?.exchange.status !== IDeliverabilityStatus.UNDELIVERABLE
    ) {
      return {
        offer: findOfferByOfferType(offers, false),
        deliverability: deliverability?.exchange.status,
      };
    }

    if (
      deliverability?.refill?.status &&
      deliverability?.refill.status !== IDeliverabilityStatus.UNDELIVERABLE
    ) {
      return {
        offer: findOfferByOfferType(offers, true),
        deliverability: deliverability?.refill.status,
      };
    }

    return undefined;
  }
  return undefined;
};

export const disableLPGSelection = (
  setLPGEnabled: (lpgEnabled: boolean) => void,
  setLPGValue: (lpgValue: boolean) => void
) => {
  setLPGValue(false);
  setLPGEnabled(false);
};

const LPG_UNABLE_TO_DELIVER_MESSAGE =
  "Sorry, we don't deliver LPG to this address.";

export const setLPGStates = (
  offerDeliverability: LPGOfferDeliverability | undefined,
  lpgValue: boolean,
  isFuelValueSelected: boolean,
  internetValue: boolean,
  setLPGEnabled: (lpgEnabled: boolean) => void,
  setLPGValue: (lpgValue: boolean) => void,
  setDismissableBannerOptions: (options: IDismissableBannerOptions) => void
) => {
  const { deliverability, offer } = { ...offerDeliverability };

  // any deliverability status to allow LPG-only selection
  if (deliverability) {
    // check for multi-services
    if (internetValue || isFuelValueSelected) {
      // LPG is only enabled if there's a cross-sell offer available and status is `DELIVERABLE`
      if (offer && deliverability === IDeliverabilityStatus.DELIVERABLE) {
        setLPGEnabled(true);
        setDismissableBannerOptions({ type: DismissableBannerType.HIDE });
      } else {
        disableLPGSelection(setLPGEnabled, setLPGValue);
        // only show the banner if LPG was previously selected
        if (lpgValue) {
          setDismissableBannerOptions({
            type: DismissableBannerType.WARNING,
            message: LPG_UNABLE_TO_DELIVER_MESSAGE,
          });
          errorForm({
            formName: FORM_NAME,
            errorMessage: `Warning: ${LPG_UNABLE_TO_DELIVER_MESSAGE}`,
            errorCode: "LPG is not deliverable with other products selected",
          });
        }
      }
    } else {
      // LPG-only means LPG is enabled
      setLPGEnabled(true);
      setDismissableBannerOptions({ type: DismissableBannerType.HIDE });
    }
  } else {
    disableLPGSelection(setLPGEnabled, setLPGValue);

    if (lpgValue) {
      if (internetValue || isFuelValueSelected) {
        setDismissableBannerOptions({
          type: DismissableBannerType.WARNING,
          message: LPG_UNABLE_TO_DELIVER_MESSAGE,
        });
        errorForm({
          formName: FORM_NAME,
          errorMessage: `Warning: ${LPG_UNABLE_TO_DELIVER_MESSAGE}`,
          errorCode: "LPG is not deliverable with other products selected",
        });
      } else {
        setDismissableBannerOptions({
          type: DismissableBannerType.ERROR,
          message: LPG_UNABLE_TO_DELIVER_MESSAGE,
        });
        errorForm({
          formName: FORM_NAME,
          errorMessage: `Error: ${LPG_UNABLE_TO_DELIVER_MESSAGE}`,
          errorCode: "LPG is not deliverable with LPG only selected",
        });
      }
    }
  }
};
